/**
=========================================================
PROJETO: PGLP – Plataforma de Gestão de Laudos Periciais
MÓDULO: Engine de Prazo Operacional
ARQUIVO: operacional.engine.js
VERSÃO: 1.0.0
DATA: 24/03/2026

DESCRIÇÃO:
Motor responsável por cálculo de prazos considerando:
✔ horário de expediente
✔ intervalos (almoço)
✔ dias úteis
✔ avanço por horas operacionais

RESPONSABILIDADE:
✔ calcular próxima tentativa
✔ ajustar horários inválidos
✔ respeitar jornada real de trabalho

REGRAS:
✔ ignora finais de semana
✔ ignora intervalos
✔ respeita expediente configurado
✔ usa fallback padrão se necessário

STATUS: PRODUÇÃO
=========================================================
*/

import { expedientePadrao } from "@/config/expediente.config";

/* =========================
   UTIL
========================= */

function parseHora(hora) {
  const [h, m] = hora.split(":").map(Number);
  return { h, m };
}

function setHora(date, hora) {
  const d = new Date(date);
  const { h, m } = parseHora(hora);
  d.setHours(h, m, 0, 0);
  return d;
}

function isFimDeSemana(date) {
  const dia = date.getDay();
  return dia === 0 || dia === 6;
}

/* =========================
   AVANÇAR PARA PRÓXIMO DIA ÚTIL
========================= */

function proximoDiaUtil(date) {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);

  while (isFimDeSemana(d)) {
    d.setDate(d.getDate() + 1);
  }

  return d;
}

/* =========================
   AJUSTAR PARA EXPEDIENTE
========================= */

function ajustarParaExpediente(date, expediente) {
  let d = new Date(date);

  const inicio = setHora(d, expediente.inicio);
  const fim = setHora(d, expediente.fim);

  // antes do expediente → joga pro início
  if (d < inicio) return inicio;

  // depois do expediente → próximo dia útil
  if (d >= fim) {
    const prox = proximoDiaUtil(d);
    return setHora(prox, expediente.inicio);
  }

  // dentro do intervalo (almoço)
  for (const intervalo of expediente.intervalos || []) {
    const iInicio = setHora(d, intervalo.inicio);
    const iFim = setHora(d, intervalo.fim);

    if (d >= iInicio && d < iFim) {
      return iFim;
    }
  }

  return d;
}

/* =========================
   CALCULAR PRÓXIMA TENTATIVA
========================= */

export function calcularProximaTentativa({
  dataBase,
  horas = 8,
  expediente = expedientePadrao,
  tipo = "AUTOMATICO" // AUTOMATICO | MANUAL
}) {

  let atual = ajustarParaExpediente(new Date(dataBase), expediente);

  let horasRestantes = horas;

  while (horasRestantes > 0) {

    if (isFimDeSemana(atual)) {
      atual = setHora(proximoDiaUtil(atual), expediente.inicio);
      continue;
    }

    const fimExpediente = setHora(atual, expediente.fim);

    // verifica intervalo
    let entrouIntervalo = false;

    for (const intervalo of expediente.intervalos || []) {
      const iInicio = setHora(atual, intervalo.inicio);
      const iFim = setHora(atual, intervalo.fim);

      if (atual < iInicio) {
        const diff = (iInicio - atual) / (1000 * 60 * 60);

        if (diff >= horasRestantes) {
          atual.setHours(atual.getHours() + horasRestantes);
          horasRestantes = 0;
          break;
        }

        horasRestantes -= diff;
        atual = iFim;
        entrouIntervalo = true;
        break;
      }

      if (atual >= iInicio && atual < iFim) {
        atual = iFim;
        entrouIntervalo = true;
        break;
      }
    }

    if (entrouIntervalo) continue;

    const diffExpediente = (fimExpediente - atual) / (1000 * 60 * 60);

    if (diffExpediente >= horasRestantes) {
      atual.setHours(atual.getHours() + horasRestantes);
      horasRestantes = 0;
    } else {
      horasRestantes -= diffExpediente;
      atual = setHora(proximoDiaUtil(atual), expediente.inicio);
    }
  }

  /* =========================
     REGRA FINAL (MANUAL vs AUTO)
  ========================= */

  if (tipo === "MANUAL") {
    const inicio = setHora(atual, expediente.inicio);

    if (atual > inicio) {
      const prox = proximoDiaUtil(atual);
      return setHora(prox, expediente.inicio);
    }
  }

  return ajustarParaExpediente(atual, expediente);
}